/**
 * Registration email domain / TLD blocklist — shared pure matcher (ENG-12,
 * GitHub #213, openspec: abuse-email-blocklist).
 *
 * Runs in three places, all server-side:
 *   - convex/users.ts getOrCreateUser  → refuses to create the users row
 *   - app/api/webhooks/clerk (user.created) → deletes the Clerk account
 *   - app/api/auth/blocked-signup       → readable-error page cleanup
 *
 * Entries come from the admin-editable `emailBlocklist` Convex table (read
 * live — no cache, so admin CRUD is effective immediately, well inside the
 * ≤ 5 minute AC). This module holds no state: it only normalises entries and
 * matches an email against them.
 *
 * Policy (MVP): every match is a HARD block. There is no "warn" tier and no
 * per-email allowlist — both are Phase 2 (see openspec design.md D2/D7).
 */

export type BlocklistKind = 'domain' | 'tld'

export interface BlocklistEntry {
  kind: BlocklistKind
  /** Normalised: lower-case, no leading `@`/`*.`/`.` — see normalizeBlocklistValue. */
  value: string
}

export interface BlocklistMatch {
  kind: BlocklistKind
  value: string
  /** The email's domain that matched, lower-cased. */
  domain: string
}

/** Machine-readable error code carried in ConvexError data / API JSON. */
export const EMAIL_BLOCKLIST_ERROR_CODE = 'EMAIL_DOMAIN_BLOCKED'

/** User-facing copy (design.md D6). English only for MVP, matching nearby auth copy. */
export const EMAIL_BLOCKLIST_ERROR_MESSAGE =
  'This email domain is not allowed. Please use a different email address to sign up.'

/** Minimum TLD seed required by #213. Stored without the leading dot. */
export const SEED_BLOCKED_TLDS: readonly string[] = ['site', 'pro']

const LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/

/**
 * Lower-cases and strips the decorations people naturally type
 * (`@evil.example`, `*.site`, `.site`, `*@evil.example`, trailing dot).
 * Returns null when the result is not a plausible domain / TLD.
 */
export function normalizeBlocklistValue(kind: BlocklistKind, raw: string): string | null {
  let v = raw.trim().toLowerCase()
  v = v.replace(/^\*@/, '').replace(/^@/, '').replace(/^\*\./, '').replace(/^\.+/, '').replace(/\.+$/, '')
  if (!v || v.includes('@') || /\s/.test(v)) return null
  const labels = v.split('.')
  if (labels.some((l) => !LABEL_RE.test(l))) return null
  if (kind === 'domain' && labels.length < 2) return null
  return v
}

/** Domain part of an email, lower-cased and trimmed; null when malformed. */
export function emailDomain(email: string): string | null {
  const at = email.lastIndexOf('@')
  if (at < 0) return null
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase()
    .replace(/\.+$/, '')
  return domain || null
}

/**
 * Returns the first blocklist entry the email matches, or null.
 *
 *   domain  — exact match or any parent domain (`a.b.tempmail.dev` ⊂ `tempmail.dev`)
 *   tld     — domain's trailing label(s) (`x.site` ⊂ `site`; `x.co.uk` ⊂ `co.uk`)
 *
 * Order of precedence: tld entries first (cheap, broadest), then domains.
 */
export function matchEmailBlocklist(
  email: string,
  entries: Iterable<BlocklistEntry>,
): BlocklistMatch | null {
  const domain = emailDomain(email)
  if (!domain) return null
  const labels = domain.split('.')
  if (labels.length < 2) return null

  const tlds = new Set<string>()
  const domains = new Set<string>()
  for (const e of entries) {
    if (!e?.value) continue
    ;(e.kind === 'tld' ? tlds : domains).add(e.value)
  }

  // Suffixes that leave at least one label in front (the registrable part).
  for (let i = 1; i < labels.length; i++) {
    const suffix = labels.slice(i).join('.')
    if (tlds.has(suffix)) return { kind: 'tld', value: suffix, domain }
  }
  for (let i = 0; i < labels.length - 1; i++) {
    const suffix = labels.slice(i).join('.')
    if (domains.has(suffix)) return { kind: 'domain', value: suffix, domain }
  }
  return null
}
